import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { UnmountClosed as ReactUnmountClosed } from "react-collapse";
import ClassNames from "classnames";
import {
  getDataAnalyticsProps,
  type DataAnalyticsAttributes,
} from "../../../utils/UserAnalyticsUtils";
import "./Collapse.scss";

export interface CollapseProps extends DataAnalyticsAttributes {
  className?: string;
  hasPadding?: boolean;
  hasTitleBorder?: boolean;
  borderless?: boolean;
  labelComponent?: ReactNode;
  children?: ReactNode;
  scrollIntoViewWhenOpen?: boolean;
  initialState?: boolean;
  label?: ReactNode;
  showOptional?: boolean;
  icon?: string;
  isLarge?: boolean;
  isColored?: boolean;
  hideBorderBottom?: boolean;
  hasRightChevron?: boolean;
  headerEndContent?: ReactNode;
  onToggle?: (isOpen: boolean) => void;
}

export interface CollapseRef {
  resetCollapseRef: () => void;
  toggleCollapse: (isOpen: boolean) => void;
}

const Collapse = forwardRef<CollapseRef, CollapseProps>(
  (
    {
      className,
      hasPadding,
      hasTitleBorder,
      borderless = false,
      labelComponent,
      children,
      scrollIntoViewWhenOpen,
      initialState = true,
      label,
      showOptional = false,
      icon,
      isLarge = false,
      isColored = false,
      hideBorderBottom = false,
      hasRightChevron = false,
      headerEndContent,
      onToggle,
      ...props
    },
    ref
  ) => {
    const collapseRef = useRef<HTMLDivElement | null>(null);
    const headerEndContentRef = useRef<HTMLDivElement | null>(null);
    const [isOpened, setIsOpened] = useState(initialState);

    // Guarded with ref/target checks instead of `stopPropagation`, which would
    // stop the click reaching the document-level delegated analytics listener.
    // The currentTarget check also covers React events crossing portals.
    const onToggleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as Node;

      if (!event.currentTarget.contains(target)) {
        return;
      }

      if (headerEndContentRef.current?.contains(target)) {
        return;
      }

      const nextIsOpened = !isOpened;

      setIsOpened(nextIsOpened);
      onToggle?.(nextIsOpened);
    };

    useImperativeHandle(
      ref,
      () => ({
        resetCollapseRef: () => {
          collapseRef.current = null;
        },
        toggleCollapse: (isOpen: boolean) => {
          setIsOpened(isOpen);
        },
      }),
      []
    );

    return (
      <>
        <div
          className={ClassNames("collapse-wrapper", className, {
            "has-padding": hasPadding,
            "has-title-border": hasTitleBorder,
            borderless,
            "hide-border-bottom": hideBorderBottom,
            "is-large": isLarge,
            "is-colored": isColored,
            "has-right-chevron": hasRightChevron,
            "has-header-end-content": !!headerEndContent,
          })}
        >
          <div
            className={`collapse-toggle`}
            onClick={onToggleClick}
            {...getDataAnalyticsProps(props)}
          >
            {!!icon && <i className={`seem-icon ${icon} collapse-icon`} />}
            {!!label && <label className="collapse-title">{label}</label>}
            {showOptional && <span className={"label-helper"}>Optional</span>}
            {!!labelComponent && labelComponent}
            {!!headerEndContent && (
              <div
                className="collapse-header-end-content"
                ref={headerEndContentRef}
              >
                {headerEndContent}
              </div>
            )}
            <div
              className={`collapse-toggle-icon-wrap ${
                isOpened ? "is-open" : "is-closed"
              }`}
            >
              <i className={`seem-icon seem-icon-right-arrow`} />
            </div>
          </div>
          <ReactUnmountClosed
            onRest={({ isFullyOpened }) => {
              if (scrollIntoViewWhenOpen && isFullyOpened) {
                collapseRef?.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
              }
            }}
            isOpened={isOpened}
          >
            {children}
          </ReactUnmountClosed>
        </div>
        {!!scrollIntoViewWhenOpen && (
          <div className={"collapse-open-scroll-dummy"} ref={collapseRef} />
        )}
      </>
    );
  }
);

export default Collapse;
